import { createSlice } from '@reduxjs/toolkit';

import { Decimal } from '~/main/utils/Decimal';
import { PaymentState } from '~/renderer/store/receipt/types';
import { isCustomerCFD } from '~/renderer/utils';
import { transformTip } from '~/renderer/utils/sale-transformer';

import type { CaseReducer, PayloadAction } from '@reduxjs/toolkit';
import type { ReceiptState, Sale, Tip } from '~/renderer/store/receipt/types';

export const initialState: ReceiptState = {
  sale: null,
  saleId: '',
  isSaleVisible: false,
  isCustomerInfoViewVisible: isCustomerCFD,
  tip: null,
};

interface UpdateSale {
  sale: Sale;
  saleId?: string;
  tip?: Tip | null;
  paymentState?: PaymentState | null;
}

interface UpdateTip {
  tipId: string;
  enteredTipAmount: number | null;
}

interface CustomerVisibiltyPayload extends Pick<ReceiptState, 'isCustomerInfoViewVisible'> {
  isSaleVisible?: boolean;
}

const updateIsSaleVisible: CaseReducer<ReceiptState, PayloadAction<boolean>> = (_state, action) => {
  const isSaleVisible = action.payload;
  return { ...initialState, isSaleVisible };
};

const updateSale: CaseReducer<ReceiptState, PayloadAction<UpdateSale>> = (state, action) => {
  state.sale = action.payload.sale;
  state.saleId = action.payload.saleId;
  state.tip = action?.payload?.tip ? transformTip(action.payload.tip) : action.payload.tip || null;
  state.paymentState = action.payload?.paymentState || null;
  state.isSaleVisible = true;
};

const updateEnteredTip: CaseReducer<ReceiptState, PayloadAction<UpdateTip>> = (state, action) => {
  const { tipId, enteredTipAmount } = action.payload;
  const tipAmountRounded = new Decimal(enteredTipAmount || 0).toFixed(2);
  const paymentTotal = new Decimal(state.tip?.paymentTotal || 0);
  const tipAmount = new Decimal(tipAmountRounded);

  const totalAmountWithTip = tipAmount.plus(paymentTotal).toNumber();
  const clonedTip = state.tip || {
    tipOptions: [],
    paymentTotal: 0,
    tippableAmount: 0,
    customTip: false,
  };
  state.tip = {
    ...clonedTip,
    enteredTip: {
      tipId,
      tipAmount: tipAmount.toNumber(),
      totalAmountWithTip,
    },
  };
};

const updatePaymentStaged: CaseReducer<ReceiptState> = state => {
  state.paymentState = PaymentState.PAYMENT_STARTED;
};

const updateSaleCompleted: CaseReducer<ReceiptState, PayloadAction<UpdateSale>> = (
  state,
  action,
) => {
  state.sale = {
    ...state.sale,
    ...action.payload.sale,
  };
  state.sale.completed = true;
  state.isSaleVisible = false;
  state.tip = null;
};

const updatePaymentStatusComplete: CaseReducer<ReceiptState, PayloadAction> = state => {
  state.tip = null;
};

const setCustomerInfoVisibility: CaseReducer<
  ReceiptState,
  PayloadAction<CustomerVisibiltyPayload>
> = (state, action) => {
  const { isCustomerInfoViewVisible, isSaleVisible = true } = action.payload;
  state.isCustomerInfoViewVisible = isCustomerInfoViewVisible;
  state.isSaleVisible = isSaleVisible;
};

const receiptSlice = createSlice({
  name: 'receipt',
  initialState,
  reducers: {
    updateSale,
    updateSaleCompleted,
    updateIsSaleVisible,
    updateEnteredTip,
    updatePaymentStaged,
    updatePaymentStatusComplete,
    setCustomerInfoVisibility,
  },
});

const { actions, reducer } = receiptSlice;
export { reducer as receiptReducer, actions as receiptActions };
